import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  motion,
  useScroll,
  useTransform,
  useMotionValue,
  useSpring,
} from 'framer-motion';
import {
  ArrowRight,
  Eye,
  Waves,
  MapPin,
  Battery,
  Quote,
  AlertTriangle,
  Lightbulb,
  Repeat,
  FlaskConical,
  Users,
  CheckCircle2,
} from 'lucide-react';
import SimplifiedModuleDiagram from '../components/SimplifiedModuleDiagram';
import InteractiveFeatureShowcase from '../components/InteractiveFeatureShowcase';
import VideoModal, { VideoThumbnail } from '../components/VideoModal';
import MagneticButton from '../components/MagneticButton';
import HeroDetectionMap from '../components/HeroDetectionMap';
import ExplainerHero from '../components/ExplainerHero';
import PressSection from '../components/PressSection';

/* ─── Motion primitives ──────────────────────────────────────── */

/**
 * Clean fade + gentle upward slide as content enters viewport.
 * This is the only scroll-triggered animation used throughout.
 */
const FadeUp = ({ children, className = '', delay = 0 }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-60px' }}
    transition={{ duration: 0.72, delay, ease: [0.25, 0, 0, 1] }}
  >
    {children}
  </motion.div>
);

/**
 * Very subtle 3-D tilt on hover — used only for interactive cards.
 * Max ±4° to keep it refined rather than playful.
 */
const TiltCard = ({ children, className }) => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [4, -4]), { stiffness: 320, damping: 32 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-4, 4]),  { stiffness: 320, damping: 32 });

  return (
    <motion.div
      className={className}
      style={{ rotateX: rx, rotateY: ry, transformPerspective: 1200 }}
      onMouseMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width  - 0.5);
        my.set((e.clientY - r.top)  / r.height - 0.5);
      }}
      onMouseLeave={() => { mx.set(0); my.set(0); }}
    >
      {children}
    </motion.div>
  );
};

/* ─── Highlight card ─────────────────────────────────────────── */
const HighlightCard = ({ item, index }) => (
  <TiltCard className="group p-6 rounded-2xl bg-gradient-to-b from-gray-800/50 to-transparent border border-gray-800/50 hover:border-blue-500/20 transition-colors duration-300 cursor-default">
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.09, ease: [0.25, 0, 0, 1] }}
    >
      <motion.div
        className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/10 flex items-center justify-center text-blue-400 mb-4 group-hover:bg-blue-500/15 group-hover:border-blue-500/20 transition-colors duration-300"
        whileHover={{ scale: 1.1, rotate: 6 }}
        transition={{ duration: 0.2, ease: [0.25, 0, 0, 1] }}
      >
        {item.icon}
      </motion.div>
      <h3 className="text-base font-semibold mb-1.5">{item.title}</h3>
      <p className="text-gray-500 text-sm leading-relaxed">{item.description}</p>
    </motion.div>
  </TiltCard>
);

/* ─── HomePage ────────────────────────────────────────────────── */

const HomePage = () => {
  const { scrollYProgress } = useScroll();
  /* Scroll progress bar spring */
  const scaleX = useSpring(scrollYProgress, { stiffness: 400, damping: 40 });

  const [isVideoOpen, setIsVideoOpen] = useState(false);

  const highlights = [
    { icon: <Eye     className="w-5 h-5" />, title: 'Forward Detection',  description: 'Senses obstacles ahead before contact' },
    { icon: <Waves   className="w-5 h-5" />, title: 'Surface Scanning',   description: 'Detects potholes, drops, and uneven ground' },
    { icon: <MapPin  className="w-5 h-5" />, title: 'Find My Support',    description: 'Locate your ClearWay using Apple Find My' },
    { icon: <Battery className="w-5 h-5" />, title: 'Reliable Power',     description: 'Designed for dependability, not complexity' },
  ];

  return (
    <div>

      {/* Scroll progress bar */}
      <motion.div
        aria-hidden="true"
        className="fixed top-0 left-0 right-0 h-[2px] z-[100]"
        style={{ scaleX, background: 'linear-gradient(90deg, #3b82f6, #60a5fa)', transformOrigin: '0% 50%' }}
      />

      {/* ══════════════════════════════════════════════════
          HERO — cinematic ClearWay explainer, then CTAs
      ══════════════════════════════════════════════════ */}
      <ExplainerHero />


      {/* ══════════════════════════════════════════════════
          TRIAL MILESTONE CALLOUT — 1A
      ══════════════════════════════════════════════════ */}
      <section className="py-10 px-6 border-y border-white/5 bg-[#0d0d0d]">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.25, 0, 0, 1] }}
          className="max-w-3xl mx-auto text-center"
        >
          <p className="text-white text-xl md:text-2xl font-semibold leading-snug mb-3">
            Every participant at our first trial wanted to use ClearWay again.
          </p>
          <p className="text-gray-400 text-sm md:text-base leading-relaxed">
            Obstacle detection confidence rated{' '}
            <span className="text-white font-medium">9.7&thinsp;/&thinsp;10</span>.
            {' '}Understood immediately — no training required.
            {' '}First formal trial at Kamla Mehta School for the Blind, Mumbai.{' '}
            <Link
              to="/impact"
              className="text-blue-400 hover:text-blue-300 transition-colors inline-flex items-center gap-1"
            >
              See the results <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </p>
        </motion.div>
      </section>


      {/* ══════════════════════════════════════════════════
          QUICK HIGHLIGHTS
      ══════════════════════════════════════════════════ */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {highlights.map((item, index) => (
              <HighlightCard key={item.title} item={item} index={index} />
            ))}
          </div>
        </div>
      </section>


      {/* ══════════════════════════════════════════════════
          PRESS — "Featured in" strip, links to /press
      ══════════════════════════════════════════════════ */}
      <PressSection />


      {/* ══════════════════════════════════════════════════
          PROBLEM → SOLUTION
      ══════════════════════════════════════════════════ */}
      <section data-testid="problem-solution-section" className="py-24 px-6 bg-[#0f0f0f]">
        <div className="max-w-6xl mx-auto">
          <FadeUp className="text-center mb-14">
            <p className="text-blue-400 text-sm font-medium tracking-widest uppercase mb-3">Why ClearWay</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight mb-4">
              From a real problem
              <br />
              <span className="text-gray-500">to a real solution.</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Built around the everyday challenges visually impaired people actually face — not assumptions.
            </p>
          </FadeUp>

          <div className="grid md:grid-cols-3 gap-6 items-stretch">
            {/* Problem */}
            <FadeUp delay={0.05}>
              <div className="h-full p-8 rounded-3xl bg-gradient-to-b from-red-500/[0.06] to-transparent border border-red-500/15">
                <div className="w-12 h-12 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center text-red-400 mb-5">
                  <AlertTriangle className="w-5 h-5" />
                </div>
                <p className="text-red-400 text-xs font-semibold tracking-widest uppercase mb-2">Problem</p>
                <h3 className="text-xl font-semibold mb-3">Urban navigation is difficult for visually impaired individuals.</h3>
                <p className="text-gray-400 leading-relaxed text-sm">
                  Traditional canes only reveal an obstacle at the moment of contact. Kerbs,
                  sudden drops, parked vehicles, and objects left on the path are found by
                  touch — often too late to react — making everyday streets, especially in
                  dense cities, unsafe and unpredictable.
                </p>
              </div>
            </FadeUp>

            {/* Solution */}
            <FadeUp delay={0.15}>
              <div className="h-full p-8 rounded-3xl bg-gradient-to-b from-blue-500/[0.08] to-transparent border border-blue-500/20 relative overflow-hidden">
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400 mb-5">
                  <Lightbulb className="w-5 h-5" />
                </div>
                <p className="text-blue-400 text-xs font-semibold tracking-widest uppercase mb-2">Solution</p>
                <h3 className="text-xl font-semibold mb-3">ClearWay — the smart mobility stick.</h3>
                <p className="text-gray-400 leading-relaxed text-sm">
                  A single integrated device that combines forward obstacle detection, downward
                  surface scanning, and Apple Find My support. Intuitive feedback warns the user
                  before contact, restoring confidence and independence on any street.
                </p>
              </div>
            </FadeUp>

            {/* Approach */}
            <FadeUp delay={0.25}>
              <div className="h-full p-8 rounded-3xl bg-gradient-to-b from-emerald-500/[0.06] to-transparent border border-emerald-500/15">
                <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400 mb-5">
                  <Repeat className="w-5 h-5" />
                </div>
                <p className="text-emerald-400 text-xs font-semibold tracking-widest uppercase mb-2">Approach</p>
                <h3 className="text-xl font-semibold mb-3">Real-world testing + iteration.</h3>
                <p className="text-gray-400 leading-relaxed text-sm">
                  Every design choice is shaped by field trials with real users in real cities.
                  We test, listen, refine, and re-test — letting feedback from the people who
                  rely on ClearWay drive every revision of the hardware and software.
                </p>
              </div>
            </FadeUp>
          </div>

          {/* Connecting flow indicator */}
          <FadeUp delay={0.35}>
            <div className="mt-10 flex items-center justify-center gap-3 text-xs text-gray-500 tracking-widest uppercase">
              <span>Problem</span>
              <ArrowRight className="w-3.5 h-3.5 text-blue-400" />
              <span>Solution</span>
              <ArrowRight className="w-3.5 h-3.5 text-blue-400" />
              <span>Iteration</span>
            </div>
          </FadeUp>
        </div>
      </section>


      {/* ══════════════════════════════════════════════════
          DETECTION MAP — interactive obstacle intelligence
      ══════════════════════════════════════════════════ */}
      <HeroDetectionMap />


      {/* ══════════════════════════════════════════════════
          VIDEO
      ══════════════════════════════════════════════════ */}
      <section className="py-24 px-6 bg-[#0f0f0f]">
        <div className="max-w-5xl mx-auto">
          <FadeUp className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold mb-4">See ClearWay in Action</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Watch how ClearWay helps users navigate their environment with confidence and independence.
            </p>
          </FadeUp>

          <FadeUp delay={0.1}>
            <div
              className="rounded-3xl overflow-hidden"
              style={{ boxShadow: '0 0 0 1px rgba(255,255,255,0.06), 0 32px 80px -16px rgba(0,0,0,0.6)' }}
            >
              <VideoThumbnail onClick={() => setIsVideoOpen(true)} />
            </div>
          </FadeUp>
        </div>
      </section>

      <VideoModal
        isOpen={isVideoOpen}
        onClose={() => setIsVideoOpen(false)}
        videoUrl="https://videos.jtlr.org/v/801gKKFlmLnFcEd8adIdQfEh6Jk014FwH7FBY3blvi9dQ/embed"
      />


      {/* ══════════════════════════════════════════════════
          PRODUCT STATEMENT
      ══════════════════════════════════════════════════ */}
      <section className="py-32 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <FadeUp>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight mb-8 leading-tight">
              One complete device.
              <br />
              <span className="text-gray-500">Simplifying mobility.</span>
            </h2>
          </FadeUp>
          <FadeUp delay={0.12}>
            <p className="text-xl text-gray-400 leading-relaxed">
              ClearWay integrates forward-facing obstacle detection, downward-facing surface
              sensing, and Apple Find My support into a single, elegant mobility stick.
              No accessories. No complexity. Just reliable assistance when you need it.
            </p>
          </FadeUp>
        </div>
      </section>


      {/* ══════════════════════════════════════════════════
          HOW IT WORKS
      ══════════════════════════════════════════════════ */}
      <section className="py-24 px-6 bg-[#0f0f0f]">
        <div className="max-w-6xl mx-auto">
          <FadeUp className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold mb-4">How It Works</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              A compact module that transforms a traditional walking stick into a smart mobility device.
            </p>
          </FadeUp>
          <FadeUp delay={0.1}>
            <SimplifiedModuleDiagram />
          </FadeUp>
        </div>
      </section>


      {/* ══════════════════════════════════════════════════
          INTERACTIVE FEATURE SHOWCASE
      ══════════════════════════════════════════════════ */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <FadeUp>
            <InteractiveFeatureShowcase />
          </FadeUp>
        </div>
      </section>


      {/* ══════════════════════════════════════════════════
          FOUNDER STORY
      ══════════════════════════════════════════════════ */}
      <section className="py-24 px-6 bg-[#0f0f0f]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">

            <FadeUp>
              <p className="text-blue-400 text-sm font-medium tracking-widest uppercase mb-4">The Story</p>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-6">
                From Science Fair
                <br />
                <span className="text-gray-500">to Real Impact</span>
              </h2>
              <div className="space-y-4 text-gray-400 leading-relaxed">
                <p>
                  ClearWay began in 2021 as a project I built for a school science fair in 7th grade.
                  I wanted to create something that addressed a real-world problem, which led me to
                  explore assistive technologies for people with visual impairments.
                </p>
                <p>
                  I&apos;m Jaiveer Talwar, and what started as a student project has grown into
                  a broader effort to make everyday navigation safer and more intuitive.
                </p>
              </div>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 mt-6 text-blue-400 hover:text-blue-300 font-medium transition-colors"
              >
                Read the full story
                <motion.span
                  animate={{ x: [0, 4, 0] }}
                  transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut', repeatDelay: 0.8 }}
                >
                  <ArrowRight className="w-4 h-4" />
                </motion.span>
              </Link>
            </FadeUp>

            <FadeUp delay={0.15}>
              <TiltCard className="p-8 rounded-3xl bg-gradient-to-br from-blue-500/[0.05] to-purple-500/[0.04] border border-gray-800/50">
                <Quote className="w-10 h-10 text-blue-400/25 mb-4" />
                <blockquote className="text-xl text-gray-300 leading-relaxed mb-6">
                  &ldquo;What started as a science fair project soon became something much more
                  meaningful. Working on ClearWay showed me how small technological improvements
                  can have a real impact when they address everyday accessibility challenges.&rdquo;
                </blockquote>
                <div className="flex items-center gap-4">
                  <motion.div
                    className="relative w-12 h-12 cursor-default"
                    whileHover="hovered"
                    initial="rest"
                  >
                    <motion.div
                      className="absolute -inset-1.5 rounded-full bg-gradient-to-br from-blue-400/40 to-purple-500/40"
                      variants={{ rest: { opacity: 0, scale: 0.75 }, hovered: { opacity: 1, scale: 1 } }}
                      transition={{ duration: 0.25, ease: [0.25, 0, 0, 1] }}
                    />
                    <div className="relative w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white font-bold text-sm">
                      JT
                    </div>
                  </motion.div>
                  <div>
                    <p className="font-medium">Jaiveer Talwar</p>
                    <p className="text-sm text-gray-500">Creator, ClearWay</p>
                  </div>
                </div>
              </TiltCard>
            </FadeUp>

          </div>
        </div>
      </section>


      {/* ══════════════════════════════════════════════════
          FIELD TRIALS / RESEARCH
      ══════════════════════════════════════════════════ */}
      <section data-testid="field-trials-section" className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <FadeUp className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-blue-500/20 bg-blue-500/[0.06] mb-6">
              <FlaskConical className="w-3.5 h-3.5 text-blue-400" />
              <span className="text-blue-400 text-xs font-medium tracking-widest uppercase">Research &amp; Field Trials</span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight mb-4">
              Tested where it matters most.
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              ClearWay&apos;s first formal trial took place at Kamla Mehta School for the Blind,
              Mumbai — not a simulation, but real students and a teacher navigating real hallways,
              staircases, and outdoor grounds. All participants had total blindness. Every one of
              them wanted to continue.
            </p>
          </FadeUp>

          {/* Trial overview cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-14">
            <FadeUp delay={0.05}>
              <div className="h-full p-6 rounded-2xl bg-gradient-to-b from-gray-800/50 to-transparent border border-gray-800/50">
                <div className="w-11 h-11 rounded-xl bg-blue-500/10 border border-blue-500/15 flex items-center justify-center text-blue-400 mb-4">
                  <FlaskConical className="w-5 h-5" />
                </div>
                <p className="text-xs text-gray-500 tracking-widest uppercase mb-1">What we tested</p>
                <h3 className="text-base font-semibold mb-2">Obstacle detection &amp; feedback</h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  Forward sensor range, surface scanning accuracy, and how clearly haptic
                  alerts communicate distance and direction in noisy urban environments.
                </p>
              </div>
            </FadeUp>

            <FadeUp delay={0.12}>
              <div className="h-full p-6 rounded-2xl bg-gradient-to-b from-gray-800/50 to-transparent border border-gray-800/50">
                <div className="w-11 h-11 rounded-xl bg-blue-500/10 border border-blue-500/15 flex items-center justify-center text-blue-400 mb-4">
                  <MapPin className="w-5 h-5" />
                </div>
                <p className="text-xs text-gray-500 tracking-widest uppercase mb-1">Where</p>
                <h3 className="text-base font-semibold mb-2">Kamla Mehta School, Mumbai</h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  School hallways, staircases, and outdoor grounds — a varied, real-world
                  environment at a school for the blind, not a controlled lab setting.
                </p>
              </div>
            </FadeUp>

            <FadeUp delay={0.19}>
              <div className="h-full p-6 rounded-2xl bg-gradient-to-b from-gray-800/50 to-transparent border border-gray-800/50">
                <div className="w-11 h-11 rounded-xl bg-blue-500/10 border border-blue-500/15 flex items-center justify-center text-blue-400 mb-4">
                  <Users className="w-5 h-5" />
                </div>
                <p className="text-xs text-gray-500 tracking-widest uppercase mb-1">Who</p>
                <h3 className="text-base font-semibold mb-2">Totally blind students &amp; a teacher</h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  Participants at Kamla Mehta School for the Blind — the target population,
                  not an approximation. White cane users testing ClearWay in their own school.
                </p>
              </div>
            </FadeUp>

            <FadeUp delay={0.26}>
              <div className="h-full p-6 rounded-2xl bg-gradient-to-b from-gray-800/50 to-transparent border border-gray-800/50">
                <div className="w-11 h-11 rounded-xl bg-blue-500/10 border border-blue-500/15 flex items-center justify-center text-blue-400 mb-4">
                  <CheckCircle2 className="w-5 h-5" />
                </div>
                <p className="text-xs text-gray-500 tracking-widest uppercase mb-1">Key findings</p>
                <h3 className="text-base font-semibold mb-2">Real, measurable gains</h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  Earlier obstacle warnings, fewer unexpected collisions, and noticeably
                  higher self-reported confidence walking unfamiliar routes.
                </p>
              </div>
            </FadeUp>
          </div>

          {/* Findings detail strip */}
          <FadeUp delay={0.1}>
            <div className="grid md:grid-cols-3 gap-6 mb-14">
              <div className="p-6 rounded-2xl bg-blue-500/[0.04] border border-blue-500/15 text-center">
                <p className="text-3xl md:text-4xl font-semibold text-white mb-1">100%</p>
                <p className="text-xs text-gray-500 tracking-widest uppercase">Wanted to use again</p>
              </div>
              <div className="p-6 rounded-2xl bg-blue-500/[0.04] border border-blue-500/15 text-center">
                <p className="text-3xl md:text-4xl font-semibold text-white mb-1">9.7/10</p>
                <p className="text-xs text-gray-500 tracking-widest uppercase">Obstacle detection confidence</p>
              </div>
              <div className="p-6 rounded-2xl bg-blue-500/[0.04] border border-blue-500/15 text-center">
                <p className="text-3xl md:text-4xl font-semibold text-white mb-1">9.3/10</p>
                <p className="text-xs text-gray-500 tracking-widest uppercase">Feedback clarity rating</p>
              </div>
            </div>
          </FadeUp>

          {/* Trial photos */}
          <FadeUp delay={0.15}>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="group relative aspect-[4/3] rounded-2xl overflow-hidden border border-gray-800/60 bg-gray-900">
                <img
                  src="/media/trial-1-kamla/stick%20in%20hallway%20in%20use.jpg"
                  alt="ClearWay in active use navigating the school hallway"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/70 to-transparent">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-200 font-medium">ClearWay in use — school hallway</p>
                    <span className="text-[10px] uppercase tracking-widest text-blue-300/80 px-2 py-0.5 rounded-full border border-blue-400/20 bg-blue-500/10">Photo</span>
                  </div>
                </div>
              </div>
              <div className="group relative aspect-[4/3] rounded-2xl overflow-hidden border border-gray-800/60 bg-gray-900">
                <img
                  src="/media/trial-1-kamla/teacher%20with%20stick.jpg"
                  alt="Ashwini Srivastav, teacher at Kamla Mehta School for the Blind, demonstrating ClearWay"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/70 to-transparent">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-200 font-medium">Ashwini Srivastav — teacher at the school</p>
                    <span className="text-[10px] uppercase tracking-widest text-blue-300/80 px-2 py-0.5 rounded-full border border-blue-400/20 bg-blue-500/10">Photo</span>
                  </div>
                </div>
              </div>
              <div className="group relative aspect-[4/3] rounded-2xl overflow-hidden border border-gray-800/60 bg-gray-900">
                <img
                  src="/media/trial-1-kamla/school%20outside.jpg"
                  alt="Outdoor trial grounds at Kamla Mehta School for the Blind, Mumbai"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/70 to-transparent">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-200 font-medium">Outdoor grounds — trial site</p>
                    <span className="text-[10px] uppercase tracking-widest text-blue-300/80 px-2 py-0.5 rounded-full border border-blue-400/20 bg-blue-500/10">Photo</span>
                  </div>
                </div>
              </div>
            </div>
            <p className="text-center text-xs text-gray-600 mt-4">
              Photos used with consent. Under-18 participants are not identifiable in any image used here.
            </p>
          </FadeUp>

          {/* Quote cards */}
          <FadeUp delay={0.2}>
            <div className="grid md:grid-cols-3 gap-6 mt-12">
              {[
                {
                  quote: 'The sound feedback was clear and the GPS tracking was reliable. It was easy to use and helpful throughout.',
                  name: 'Ashwini Srivastav',
                  role: 'Teacher — Kamla Mehta School for the Blind',
                  accent: true,
                },
                {
                  quote: 'Walking with it felt natural. Being able to sense what was ahead — people, obstacles — gave me a new kind of confidence.',
                  name: 'Anjali',
                  role: 'Student',
                  accent: false,
                },
                {
                  quote: "The spatial awareness it gave me was unlike anything I'd had before. This is going to give us real independence.",
                  name: 'Komal',
                  role: 'Student',
                  accent: false,
                },
              ].map((q) => (
                <div
                  key={q.name}
                  className={`p-6 rounded-2xl border flex flex-col ${
                    q.accent
                      ? 'bg-gradient-to-b from-blue-500/[0.06] to-transparent border-blue-500/20'
                      : 'bg-gradient-to-b from-gray-800/30 to-transparent border-gray-800/50'
                  }`}
                >
                  <Quote className={`w-6 h-6 mb-3 flex-shrink-0 ${q.accent ? 'text-blue-400/40' : 'text-gray-600'}`} />
                  <p className="text-gray-300 text-sm leading-relaxed flex-1 mb-4">
                    &ldquo;{q.quote}&rdquo;
                  </p>
                  <div className={`pt-3 border-t ${q.accent ? 'border-blue-500/15' : 'border-gray-800/60'}`}>
                    <p className="text-white text-sm font-semibold">{q.name}</p>
                    <p className={`text-xs mt-0.5 ${q.accent ? 'text-blue-400' : 'text-gray-500'}`}>{q.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </FadeUp>
        </div>
      </section>


      {/* ══════════════════════════════════════════════════
          CTA
      ══════════════════════════════════════════════════ */}
      <section className="py-32 px-6 relative overflow-hidden">
        {/* Single soft glow — same restraint as hero */}
        <div
          aria-hidden="true"
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
          style={{
            width: 560,
            height: 560,
            background: 'radial-gradient(circle, rgba(59,130,246,0.07) 0%, transparent 65%)',
          }}
        />

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <FadeUp>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6">
              Interested in ClearWay?
            </h2>
            <p className="text-gray-400 text-lg mb-10 max-w-2xl mx-auto">
              Whether you represent an organization, healthcare provider, or accessibility initiative,
              we welcome the opportunity to discuss how ClearWay can help.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <MagneticButton>
                <motion.div whileTap={{ scale: 0.96 }}>
                  <Link
                    to="/shop"
                    className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-white text-black font-medium hover:bg-gray-100 transition-colors"
                  >
                    Shop Now
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </motion.div>
              </MagneticButton>
              <MagneticButton>
                <motion.div whileTap={{ scale: 0.96 }}>
                  <Link
                    to="/partner"
                    className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-blue-500 hover:bg-blue-600 text-white font-medium transition-colors"
                  >
                    Partner With Us
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </motion.div>
              </MagneticButton>
            </div>
          </FadeUp>
        </div>
      </section>

    </div>
  );
};

export default HomePage;
